import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { Check } from 'lucide-react';
import { Button } from 'antd';
import User from '../../models/User';
import ApplicationStepper from './components/ApplicationStepper';

const ApplicationSaved = () => {
    const navigate = useNavigate();
    const { auth } = useAuth();

    const talentUser = useMemo(() => User.make({ ...auth }), [auth]);

    const steps = useMemo(() => {
        const allSteps = talentUser.getApplicationStepsForTalent?.() || [];
        return allSteps
            .filter(step => step.StepTitle && step.StepInfo)
            .map(step => ({
                title: step.StepTitle,
                path: step.path,
                completed: step.hasCompleted?.(talentUser) || false,
                required: step.NeedsCompletion?.(talentUser) || false,
            }));
    }, [talentUser]);

    const currentStep = useMemo(() => talentUser.currentApplicationStep(), [talentUser]);

    const completedCount = steps.filter(s => s.completed).length;

    const handleResume = () => {
        navigate(`/talent/application${currentStep?.path || '/get-started'}`);
    };

    return (
        <div className="min-h-[70vh] flex flex-col items-center">
            {!auth.isProfileApproved?.() && (
                <div className="pt-2.5 pb-3 bg-white border-y w-full">
                    <ApplicationStepper />
                </div>
            )}

            <div className="w-full max-w-xl mx-auto py-12 px-4 flex flex-col items-center gap-8">
                <div className="flex flex-col items-center gap-3 text-center">
                    <h2 className="text-2xl font-bold text-secondary">Your application is saved</h2>
                    <p className="text-gray-600">
                        You've completed {completedCount} of {steps.length} steps. Pick up right where you left off.
                    </p>
                </div>

                <div className="w-full border border-gray-200 rounded-lg bg-white divide-y divide-gray-200">
                    {steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                            {step.completed ? (
                                <div className="flex items-center justify-center rounded-full bg-success h-6 w-6 flex-shrink-0">
                                    <Check className="h-4 w-4 text-white" />
                                </div>
                            ) : (
                                <div className="flex items-center justify-center rounded-full border-2 border-gray-300 h-6 w-6 flex-shrink-0">
                                    <span className="text-xs text-gray-400 font-semibold">{i + 1}</span>
                                </div>
                            )}
                            <span className={`text-sm font-medium ${step.completed ? 'text-gray-500' : 'text-gray-800'}`}>
                                {step.title}
                            </span>
                            {step.completed ? (
                                <span className="ml-auto text-xs text-success font-medium">Done</span>
                            ) : (
                                <>
                                    {step.required ? (
                                        <span className="ml-auto text-xs text-gray-400 font-medium">Required</span>
                                    ) : (
                                        <span className="ml-auto text-xs text-secondary font-medium">Optional</span>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>

                <Button
                    type="primary"
                    size="large"
                    onClick={handleResume}
                    className="w-full max-w-xs"
                >
                    Finish now
                </Button>
            </div>
        </div>
    );
};

export default ApplicationSaved;
